import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Edit2,
  Plus,
  AlertCircle,
  Clock,
  ToggleLeft,
  ToggleRight,
  Eye,
  DollarSign,
  Calendar,
} from "react-feather";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import {
  createOvertimePolicyApi,
  listOvertimePoliciesApi,
  updateOvertimePolicyApi,
  viewOvertimePolicyApi,
} from "../../../../Api/overtimePolicyApi";
import { viewOrganizationApi } from "../../../../Api/OrganizationApi";
import BaseModal from "../../../../BaseComponents/BaseModal";
import BaseInput from "../../../../BaseComponents/BaseInput";
import BaseSelect from "../../../../BaseComponents/BaseSelect";
import TableContainer from "../../../../BaseComponents/BaseTable";
import BaseTooltip from "../../../../BaseComponents/BaseTooltip";
import BaseButton from "../../../../BaseComponents/BaseButton";
import {
  checkStatusCodeSuccess,
  handleArrayResponse,
  getLocalItem,
  rendorBackButton,
} from "../../../../Constant/common";
import { Action, Add, Edit, Search, Submit, Update } from "../../../../Constant";
import {
  InputPlaceHolder,
  SelectPlaceHolder,
  validationMessages,
} from "../../../../Constant/validation";
import { SrNo } from "../../../../Constant/Position/position";
import { overtimePolicyTitle } from "../../../../Constant/title";
import { usePageLoader } from "../../../../_helper/loading/LoadingContext";
import {
  overtimeLabel,
  calculationTypeOptions,
  approvalTypeOptions,
  compoffRateOptions,
  OT_COMP_TYPE,
} from "../../../../Constant/HR/overtimeConstants";

// -- Helper: section heading --------------------------------------------------
const SectionHeading = ({ icon: Icon, title }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: "rgba(99,102,241,0.1)",
        border: "1px solid rgba(99,102,241,0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon size={13} strokeWidth={2.5} style={{ color: "var(--brand-indigo)" }} />
    </div>
    <span
      style={{
        fontSize: "0.8125rem",
        fontWeight: 700,
        color: "var(--text-primary)",
        letterSpacing: "0.02em",
        textTransform: "uppercase",
      }}
    >
      {title}
    </span>
  </div>
);

// -- Helper: toggle row -------------------------------------------------------
const ToggleRow = ({ label, description, checked, onChange }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 14px",
      borderRadius: 10,
      background: checked ? "rgba(99,102,241,0.06)" : "var(--glass-bg)",
      border: `1px solid ${checked ? "rgba(99,102,241,0.2)" : "var(--glass-border)"}`,
      transition: "all 0.2s",
    }}
  >
    <div>
      <div style={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--text-primary)" }}>
        {label}
      </div>
      {description && (
        <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 2 }}>
          {description}
        </div>
      )}
    </div>
    <button
      type="button"
      onClick={onChange}
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: 0,
        display: "flex",
        alignItems: "center",
      }}
    >
      {checked ? (
        <ToggleRight size={28} style={{ color: "var(--brand-indigo)" }} />
      ) : (
        <ToggleLeft size={28} style={{ color: "var(--text-muted)" }} />
      )}
    </button>
  </div>
);

// -- Main component -----------------------------------------------------------
const OvertimePolicy = ({ isOnboarding = false, onSuccess }) => {
  const navigate = useNavigate();
  // -- State ------------------------------------------------------------------
  const [list, setList] = useState([]);
  const [searchedList, setSearchedList] = useState([]);
  const [addEditModal, setAddEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [toggleModal, setToggleModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [btnLoader, setBtnLoader] = useState(false);
  const [toggleLoader, setToggleLoader] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [customPageSize, setCustomPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState();
  const [totalRecords, setTotalRecords] = useState();
  const [totalNumberOfRows, setTotalNumberOfRows] = useState();
  const [searchValue, setSearchValue] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [columnName, setColumnName] = useState("id");
  const [orgCompType, setOrgCompType] = useState(OT_COMP_TYPE.PAYMENT);
  const [orgLoading, setOrgLoading] = useState(true);

  const { setLoading } = usePageLoader();

  // -- Set page title ---------------------------------------------------------
  useEffect(() => {
    document.title = overtimePolicyTitle;
  }, []);

  // -- Fetch org compensation type --------------------------------------------
  useEffect(() => {
    const orgId = getLocalItem("onboarding_org_id") || getLocalItem("organizationId");
    if (!orgId) {
      setOrgLoading(false);
      return;
    }
    viewOrganizationApi(orgId)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          const type = resp?.data?.organization?.overtime_policy_type;
          if (type) setOrgCompType(type);
        }
      })
      .catch(() => {})
      .finally(() => setOrgLoading(false));
  }, []);

  // -- Fetch list -------------------------------------------------------------
  const fetchList = useCallback(() => {
    setLoading(true);
    const orgId = getLocalItem("onboarding_org_id") || getLocalItem("organizationId");
    const payload = {
      order: [columnName, sortOrder],
      pageNumber: currentPage,
      pageSize: customPageSize,
      search: searchValue,
      ...(orgId ? { condition: { organization_id: Number(orgId) } } : {}),
    };
    listOvertimePoliciesApi(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setList(resp?.data?.data || []);
          setSearchedList(resp?.data?.data || []);
          setTotalPages(resp?.data?.totalPages);
          setTotalRecords(resp?.data?.totalRecordsCount);
          setTotalNumberOfRows(resp?.data?.limit);
          setCurrentPage(resp?.data?.currentPage);
        } else {
          setSearchedList([]);
        }
      })
      .catch(() => setSearchedList([]))
      .finally(() => setLoading(false));
  }, [columnName, currentPage, customPageSize, searchValue, sortOrder, setLoading]);

  useEffect(() => {
    if (!isOnboarding) {
      fetchList();
    }
  }, [fetchList, isOnboarding]);

  useEffect(() => {
    if (searchValue === "") setCurrentPage(1);
  }, [searchValue]);

  // -- Formik -----------------------------------------------------------------
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      policy_name: editData?.policy_name ?? "",
      description: editData?.description ?? "",
      minimum_ot_threshold:
        editData?.minimum_ot_threshold != null ? Number(editData.minimum_ot_threshold) : 0.5,
      max_ot_per_day: editData?.max_ot_per_day != null ? Number(editData.max_ot_per_day) : 4,
      weekly_ot_limit: editData?.weekly_ot_limit != null ? Number(editData.weekly_ot_limit) : 20,
      calculation_type: editData?.calculation_type ?? "",
      approval_type: editData?.approval_type ?? "",
      rounding_type: "Exact Minutes",
      weekend_multiplier:
        editData?.weekend_multiplier != null ? Number(editData.weekend_multiplier) : 1.5,
      holiday_multiplier:
        editData?.holiday_multiplier != null ? Number(editData.holiday_multiplier) : 2,
      compoff_rate: editData?.compoff_rate != null ? Number(editData.compoff_rate) : 1.5,
      auto_ot: editData?.auto_ot ?? false,
      requires_approval: editData?.requires_approval ?? true,
      is_active: editData?.is_active ?? false,
    },
    validationSchema: yup.object({
      policy_name: yup
        .string()
        .required(validationMessages.required(overtimeLabel.policyName))
        .max(100),
      description: yup.string().nullable().max(500),
      minimum_ot_threshold: yup
        .number()
        .required(validationMessages.required(overtimeLabel.minOtThreshold))
        .min(0)
        .max(4),
      max_ot_per_day: yup
        .number()
        .required(validationMessages.required(overtimeLabel.maxOtPerDay))
        .min(0)
        .max(12),
      weekly_ot_limit: yup
        .number()
        .required(validationMessages.required(overtimeLabel.weeklyOtLimit))
        .min(0)
        .max(60, "Weekly OT limit must be less than or equal to 60"),
      calculation_type: yup
        .string()
        .required(validationMessages.required(overtimeLabel.calculationType)),
      approval_type: yup.string().required(validationMessages.required(overtimeLabel.approvalType)),
      rounding_type: yup.string().required(validationMessages.required(overtimeLabel.roundingType)),
      weekend_multiplier: yup.number().nullable(),
      holiday_multiplier: yup.number().nullable(),
      compoff_rate: yup.number().nullable(),
    }),
    onSubmit: (values) => {
      if (!values.auto_ot && !values.requires_approval) {
        toast.error(overtimeLabel.autoOrApproval);
        return;
      }
      setBtnLoader(true);
      const isPayment = orgCompType === OT_COMP_TYPE.PAYMENT;
      const orgId = getLocalItem("onboarding_org_id") || getLocalItem("organizationId");
      const payload = {
        policy_name: values.policy_name,
        description: values.description || null,
        minimum_ot_threshold: values.minimum_ot_threshold,
        max_ot_per_day: values.max_ot_per_day,
        weekly_ot_limit: values.weekly_ot_limit,
        calculation_type: values.calculation_type,
        approval_type: values.approval_type,
        rounding_type: "Exact Minutes",
        auto_ot: values.auto_ot,
        requires_approval: values.requires_approval,
        is_active: values.is_active,
        ...(orgId ? { organization_id: Number(orgId) } : {}),
        ...(isPayment
          ? {
              weekend_multiplier: values.weekend_multiplier,
              holiday_multiplier: values.holiday_multiplier,
            }
          : { compoff_rate: values.compoff_rate }),
      };
      const apiCall = editId
        ? updateOvertimePolicyApi(editId, payload)
        : createOvertimePolicyApi(payload);
      apiCall
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            toast.success(resp.message);
            if (isOnboarding && onSuccess) {
              onSuccess(resp?.data?.id ?? resp?.data);
            } else {
              fetchList();
              formik.resetForm();
              setAddEditModal(false);
              setEditId(null);
              setEditData(null);
            }
          } else {
            toast.error(resp?.message);
          }
        })
        .catch((err) => handleArrayResponse(err))
        .finally(() => setBtnLoader(false));
    },
  });

  // -- Toggle handler ---------------------------------------------------------
  const handleToggleConfirm = () => {
    if (!toggleTarget) return;
    setToggleLoader(true);
    const orgId = getLocalItem("onboarding_org_id") || getLocalItem("organizationId");
    // Fetch full policy data first, then update with is_active flipped
    viewOvertimePolicyApi(toggleTarget.id, orgId)
      .then((viewResp) => {
        const policy = Array.isArray(viewResp?.data)
          ? viewResp.data[0]
          : viewResp?.data?.overtimePolicy || viewResp?.data;
        const payload = {
          policy_name: policy?.policy_name,
          description: policy?.description || null,
          minimum_ot_threshold:
            policy?.minimum_ot_threshold != null ? Number(policy.minimum_ot_threshold) : undefined,
          max_ot_per_day:
            policy?.max_ot_per_day != null ? Number(policy.max_ot_per_day) : undefined,
          weekly_ot_limit:
            policy?.weekly_ot_limit != null ? Number(policy.weekly_ot_limit) : undefined,
          calculation_type: policy?.calculation_type,
          approval_type: policy?.approval_type,
          rounding_type: policy?.rounding_type,
          weekend_multiplier:
            policy?.weekend_multiplier != null ? Number(policy.weekend_multiplier) : undefined,
          holiday_multiplier:
            policy?.holiday_multiplier != null ? Number(policy.holiday_multiplier) : undefined,
          auto_ot: policy?.auto_ot,
          requires_approval: policy?.requires_approval,
          organization_id: policy?.organization_id,
          is_active: !toggleTarget.is_active,
        };
        return updateOvertimePolicyApi(toggleTarget.id, payload);
      })
      .then((resp) => {
        if (resp?.statusCode === 200 || resp?.statusCode === 201 || resp?.statusCode === 202) {
          toast.success(resp?.message);
          fetchList();
        } else {
          toast.error(resp?.message || "Failed to update policy status");
        }
      })
      .catch((err) => handleArrayResponse(err))
      .finally(() => {
        setToggleLoader(false);
        setToggleModal(false);
        setToggleTarget(null);
      });
  };

  // -- Table event handlers ---------------------------------------------------
  const handleFetchData = (page) => setCurrentPage(page);
  const handleSearchValueChange = (value) => {
    if (value !== searchValue) {
      setSearchValue(value);
      setCurrentPage(1);
    }
  };
  const handleFetchSorting = (page, id, order) => {
    setCurrentPage(page);
    setColumnName(id);
    setSortOrder(order);
  };

  // -- Open add/edit modal ----------------------------------------------------
  const openAddModal = () => {
    setEditId(null);
    setEditData(null);
    formik.resetForm();
    setAddEditModal(true);
  };

  const openEditModal = (row) => {
    const orgId = getLocalItem("onboarding_org_id") || getLocalItem("organizationId");
    setEditId(row.id);
    // Optimistically prefill from list data while the full fetch loads
    setEditData(row);
    setAddEditModal(true);
    // Fetch complete policy data from the view endpoint
    viewOvertimePolicyApi(row.id, orgId)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          const policy = Array.isArray(resp?.data)
            ? resp.data[0]
            : resp?.data?.overtimePolicy || resp?.data;
          if (policy) setEditData(policy);
        }
      })
      .catch(() => {
        // Keep the list data as fallback — modal is already open
      });
  };

  // -- Columns ----------------------------------------------------------------
  const columns = useMemo(
    () => [
      {
        header: SrNo,
        accessorKey: "id",
        enableColumnFilter: false,
        cell: (cell) => (currentPage - 1) * customPageSize + cell.row.index + 1,
      },
      {
        header: overtimeLabel.policyName,
        accessorKey: "policy_name",
        enableColumnFilter: false,
        cell: (cell) => (
          <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{cell.getValue()}</span>
        ),
      },
      {
        header: overtimeLabel.calculationType,
        accessorKey: "calculation_type",
        enableColumnFilter: false,
        cell: (cell) => (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "3px 10px",
              borderRadius: 8,
              fontSize: "0.75rem",
              fontWeight: 600,
              background: "rgba(99,102,241,0.1)",
              color: "var(--brand-indigo)",
              border: "1px solid rgba(99,102,241,0.2)",
            }}
          >
            <Clock size={10} strokeWidth={2.5} />
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "Compensation",
        accessorKey: "compoff_rate",
        enableColumnFilter: false,
        cell: () => {
          const isPayment = orgCompType === OT_COMP_TYPE.PAYMENT;
          return (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "3px 10px",
                borderRadius: 8,
                fontSize: "0.75rem",
                fontWeight: 600,
                background: isPayment ? "rgba(16,185,129,0.1)" : "rgba(99,102,241,0.1)",
                color: isPayment ? "#10b981" : "var(--brand-indigo)",
                border: `1px solid ${isPayment ? "rgba(16,185,129,0.2)" : "rgba(99,102,241,0.2)"}`,
              }}
            >
              {isPayment ? "Payment" : "Comp-Off"}
            </span>
          );
        },
      },
      {
        header: overtimeLabel.isActive,
        accessorKey: "is_active",
        enableColumnFilter: false,
        cell: (cell) => (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "3px 10px",
              borderRadius: 8,
              fontSize: "0.75rem",
              fontWeight: 600,
              background: cell.getValue() ? "rgba(16,185,129,0.1)" : "rgba(244,63,94,0.1)",
              color: cell.getValue() ? "#10b981" : "#f43f5e",
              border: `1px solid ${cell.getValue() ? "rgba(16,185,129,0.2)" : "rgba(244,63,94,0.2)"}`,
            }}
          >
            {cell.getValue() ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        header: Action,
        accessorKey: "action",
        enableColumnFilter: false,
        cell: (cell) => {
          const row = cell.row.original;
          return (
            <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
              <BaseTooltip label={overtimeLabel.viewPolicy} color="var(--brand-indigo)">
                <button
                  type="button"
                  onClick={() => {
                    const orgId =
                      getLocalItem("onboarding_org_id") || getLocalItem("organizationId");
                    setViewData(row);
                    setViewModal(true);
                    viewOvertimePolicyApi(row.id, orgId)
                      .then((resp) => {
                        if (checkStatusCodeSuccess(resp?.statusCode)) {
                          const policy = Array.isArray(resp?.data)
                            ? resp.data[0]
                            : resp?.data?.overtimePolicy || resp?.data;
                          if (policy) setViewData(policy);
                        }
                      })
                      .catch(() => {});
                  }}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    border: "1px solid var(--glass-border)",
                    background: "rgba(99,102,241,0.08)",
                    color: "var(--brand-indigo)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <Eye size={13} strokeWidth={2} />
                </button>
              </BaseTooltip>
              <BaseTooltip label={overtimeLabel.editPolicy} color="#f59e0b">
                <button
                  type="button"
                  onClick={() => openEditModal(row)}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    border: "1px solid var(--glass-border)",
                    background: "rgba(245,158,11,0.08)",
                    color: "#f59e0b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <Edit2 size={13} strokeWidth={2} />
                </button>
              </BaseTooltip>
              <BaseTooltip
                label={
                  row.is_active ? overtimeLabel.deactivatePolicy : overtimeLabel.activatePolicy
                }
                color={row.is_active ? "#f43f5e" : "#10b981"}
              >
                <button
                  type="button"
                  onClick={() => {
                    setToggleTarget(row);
                    setToggleModal(true);
                  }}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    border: "1px solid var(--glass-border)",
                    background: row.is_active ? "rgba(244,63,94,0.08)" : "rgba(16,185,129,0.08)",
                    color: row.is_active ? "#f43f5e" : "#10b981",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  {row.is_active ? (
                    <ToggleRight size={13} strokeWidth={2} />
                  ) : (
                    <ToggleLeft size={13} strokeWidth={2} />
                  )}
                </button>
              </BaseTooltip>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgCompType, currentPage, customPageSize],
  );

  // -- Derived values ---------------------------------------------------------
  const isPayment = orgCompType === OT_COMP_TYPE.PAYMENT;

  // -- Render -----------------------------------------------------------------
  if (isOnboarding) {
    return (
      <form onSubmit={formik.handleSubmit}>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {orgCompType === OT_COMP_TYPE.COMPOFF && (
            <div
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                background: "rgba(245, 158, 11, 0.08)",
                border: "1px solid rgba(245, 158, 11, 0.25)",
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
              }}
            >
              <AlertCircle
                size={16}
                strokeWidth={2.5}
                style={{ color: "#d97706", flexShrink: 0, marginTop: 1 }}
              />
              <span
                style={{
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: "#d97706",
                  lineHeight: "1.4",
                }}
              >
                Created Overtime policy only apply if super admin select Overtime policy to Overtime
                Payment.
              </span>
            </div>
          )}
          {/* Section 1 Basic Information */}
          <div
            style={{
              padding: "16px",
              borderRadius: 12,
              background: "var(--glass-bg)",
              border: "1px solid var(--glass-border)",
            }}
          >
            <SectionHeading icon={AlertCircle} title={overtimeLabel.basicInfo} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
              <BaseInput
                name="policy_name"
                label={overtimeLabel.policyName}
                placeholder={InputPlaceHolder(overtimeLabel.policyName)}
                value={formik.values.policy_name}
                handleChange={formik.handleChange}
                handleBlur={formik.handleBlur}
                touched={formik.touched.policy_name}
                error={formik.errors.policy_name}
                required
              />
              <BaseSelect
                name="is_active"
                label={overtimeLabel.isActive}
                placeholder={SelectPlaceHolder(overtimeLabel.isActive)}
                options={[
                  { value: "true", label: "Active" },
                  { value: "false", label: "Inactive" },
                ]}
                value={String(formik.values.is_active)}
                handleChange={(field, val) => formik.setFieldValue(field, val === "true")}
                handleBlur={formik.handleBlur}
                touched={formik.touched.is_active}
                error={formik.errors.is_active}
              />
              <div style={{ gridColumn: "1 / -1" }}>
                <BaseInput
                  name="description"
                  label={overtimeLabel.description}
                  placeholder={InputPlaceHolder(overtimeLabel.description)}
                  value={formik.values.description}
                  handleChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.description}
                  error={formik.errors.description}
                />
              </div>
            </div>
          </div>

          {/* Section 2 — Thresholds & Limits */}
          <div
            style={{
              padding: "16px",
              borderRadius: 12,
              background: "var(--glass-bg)",
              border: "1px solid var(--glass-border)",
            }}
          >
            <SectionHeading icon={Clock} title={overtimeLabel.thresholds} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
              <BaseInput
                name="minimum_ot_threshold"
                label={overtimeLabel.minOtThreshold}
                type="number"
                placeholder={InputPlaceHolder(overtimeLabel.minOtThreshold)}
                value={formik.values.minimum_ot_threshold}
                handleChange={formik.handleChange}
                handleBlur={formik.handleBlur}
                touched={formik.touched.minimum_ot_threshold}
                error={formik.errors.minimum_ot_threshold}
                min={0}
                max={4}
                step={0.5}
                required
              />
              <BaseInput
                name="max_ot_per_day"
                label={overtimeLabel.maxOtPerDay}
                type="number"
                placeholder={InputPlaceHolder(overtimeLabel.maxOtPerDay)}
                value={formik.values.max_ot_per_day}
                handleChange={formik.handleChange}
                handleBlur={formik.handleBlur}
                touched={formik.touched.max_ot_per_day}
                error={formik.errors.max_ot_per_day}
                min={0}
                max={12}
                step={0.5}
                required
              />
              <BaseInput
                name="weekly_ot_limit"
                label={overtimeLabel.weeklyOtLimit}
                type="number"
                placeholder={InputPlaceHolder(overtimeLabel.weeklyOtLimit)}
                value={formik.values.weekly_ot_limit}
                handleChange={formik.handleChange}
                handleBlur={formik.handleBlur}
                touched={formik.touched.weekly_ot_limit}
                error={formik.errors.weekly_ot_limit}
                min={0}
                max={60}
                step={1}
                required
              />
            </div>
          </div>

          {/* Section 3 — Calculation Settings */}
          <div
            style={{
              padding: "16px",
              borderRadius: 12,
              background: "var(--glass-bg)",
              border: "1px solid var(--glass-border)",
            }}
          >
            <SectionHeading icon={AlertCircle} title={overtimeLabel.calculation} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
              <BaseSelect
                name="calculation_type"
                label={overtimeLabel.calculationType}
                placeholder={SelectPlaceHolder(overtimeLabel.calculationType)}
                options={calculationTypeOptions.map((o) => ({ value: o.value, label: o.label }))}
                value={formik.values.calculation_type}
                handleChange={formik.setFieldValue}
                handleBlur={formik.handleBlur}
                touched={formik.touched.calculation_type}
                error={formik.errors.calculation_type}
                required
              />
              <BaseSelect
                name="approval_type"
                label={overtimeLabel.approvalType}
                placeholder={SelectPlaceHolder(overtimeLabel.approvalType)}
                options={approvalTypeOptions.map((o) => ({ value: o.value, label: o.label }))}
                value={formik.values.approval_type}
                handleChange={formik.setFieldValue}
                handleBlur={formik.handleBlur}
                touched={formik.touched.approval_type}
                error={formik.errors.approval_type}
                required
              />
            </div>
          </div>

          {/* Section 4 — Compensation (only in compoff mode) */}
          {!isPayment && (
            <div
              style={{
                padding: "16px",
                borderRadius: 12,
                background: "rgba(99,102,241,0.04)",
                border: "1px solid rgba(99,102,241,0.15)",
              }}
            >
              <SectionHeading icon={Calendar} title={overtimeLabel.compensation} />
              <div>
                <BaseSelect
                  name="compoff_rate"
                  label={overtimeLabel.compoffRate}
                  placeholder={SelectPlaceHolder(overtimeLabel.compoffRate)}
                  options={compoffRateOptions.map((o) => ({
                    value: String(o.value),
                    label: o.label,
                  }))}
                  value={String(formik.values.compoff_rate)}
                  handleChange={(field, val) =>
                    formik.setFieldValue(field, val ? Number(val) : 1.5)
                  }
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.compoff_rate}
                  error={formik.errors.compoff_rate}
                  required
                />
                <p
                  style={{
                    margin: "4px 0 0",
                    fontSize: "0.75rem",
                    color: "var(--text-muted)",
                    lineHeight: 1.5,
                  }}
                >
                  {overtimeLabel.compoffRateDesc}
                </p>
              </div>
            </div>
          )}

          {/* Section 5 — Approval & Automation */}
          <div
            style={{
              padding: "16px",
              borderRadius: 12,
              background: "var(--glass-bg)",
              border: "1px solid var(--glass-border)",
            }}
          >
            <SectionHeading icon={AlertCircle} title={overtimeLabel.approval} />
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <ToggleRow
                label={overtimeLabel.autoOt}
                description="Automatically record overtime from attendance without manual entry"
                checked={formik.values.auto_ot}
                onChange={() => formik.setFieldValue("auto_ot", !formik.values.auto_ot)}
              />
              <ToggleRow
                label={overtimeLabel.requiresApproval}
                description="Overtime must be approved by a manager before it is processed"
                checked={formik.values.requires_approval}
                onChange={() =>
                  formik.setFieldValue("requires_approval", !formik.values.requires_approval)
                }
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
          <BaseButton color="primary" type="submit" loader={btnLoader} disabled={btnLoader}>
            {Submit}
          </BaseButton>
        </div>
      </form>
    );
  }

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* -- Page Header -- */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "1.375rem",
              fontWeight: 800,
              margin: 0,
              color: "var(--text-primary)",
            }}
          >
            {overtimeLabel.pageTitle}
          </h1>
          <p style={{ margin: "2px 0 8px", fontSize: "0.8125rem", color: "var(--text-muted)" }}>
            {overtimeLabel.pageSubtitle}
          </p>
          {/* Compensation type badge */}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 12px",
              borderRadius: 999,
              fontSize: "0.75rem",
              fontWeight: 700,
              background: isPayment ? "rgba(16,185,129,0.1)" : "rgba(99,102,241,0.1)",
              color: isPayment ? "#10b981" : "var(--brand-indigo)",
              border: `1px solid ${isPayment ? "rgba(16,185,129,0.25)" : "rgba(99,102,241,0.25)"}`,
            }}
          >
            {isPayment ? (
              <>
                <DollarSign size={11} strokeWidth={2.5} /> {overtimeLabel.compensationPayment}
              </>
            ) : (
              <>
                <Calendar size={11} strokeWidth={2.5} /> {overtimeLabel.compensationCompoff}
              </>
            )}
          </span>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            onClick={openAddModal}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              borderRadius: 10,
              background: "var(--brand-indigo)",
              border: "none",
              color: "#fff",
              fontSize: "0.875rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <Plus size={15} strokeWidth={2.5} /> {Add}
          </button>
          {rendorBackButton(() => navigate("/setting"))}
        </div>
      </div>

      {/* -- Info Banner -- */}
      {orgLoading ? (
        <div
          style={{
            padding: "14px 18px",
            borderRadius: 16,
            background: "var(--glass-bg)",
            border: "1px solid var(--glass-border)",
            height: 64,
          }}
        />
      ) : (
        <div
          style={{
            padding: "14px 18px",
            borderRadius: 16,
            background: isPayment ? "rgba(16,185,129,0.06)" : "rgba(99,102,241,0.06)",
            border: `1px solid ${isPayment ? "rgba(16,185,129,0.15)" : "rgba(99,102,241,0.15)"}`,
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              flexShrink: 0,
              background: isPayment ? "rgba(16,185,129,0.1)" : "rgba(99,102,241,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isPayment ? (
              <DollarSign size={15} strokeWidth={2.5} style={{ color: "#10b981" }} />
            ) : (
              <Calendar size={15} strokeWidth={2.5} style={{ color: "var(--brand-indigo)" }} />
            )}
          </div>
          <div>
            <div
              style={{
                fontSize: "0.8125rem",
                fontWeight: 700,
                color: "var(--text-primary)",
                marginBottom: 2,
              }}
            >
              {overtimeLabel.howItWorks}
            </div>
            <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", lineHeight: 1.6 }}>
              {isPayment
                ? overtimeLabel.howItWorksDescPayment
                : overtimeLabel.howItWorksDescCompoff}
            </div>
          </div>
        </div>
      )}

      {/* -- Toggle Confirmation Modal -- */}
      <BaseModal
        isOpen={toggleModal}
        title={overtimeLabel.confirmToggleTitle}
        toggler={() => {
          setToggleModal(false);
          setToggleTarget(null);
        }}
        submit={handleToggleConfirm}
        submitText={
          toggleTarget?.is_active ? overtimeLabel.deactivatePolicy : overtimeLabel.activatePolicy
        }
        disabled={toggleLoader}
        loader={toggleLoader}
      >
        {toggleTarget &&
          overtimeLabel.confirmToggleBody(
            toggleTarget.policy_name,
            toggleTarget.is_active ? "deactivate" : "activate",
          )}
      </BaseModal>

      {/* -- Add / Edit Modal -- */}
      <BaseModal
        isOpen={addEditModal}
        size="lg"
        title={editId ? `${Edit} ${overtimeLabel.pageTitle}` : `${Add} ${overtimeLabel.pageTitle}`}
        toggler={() => {
          setAddEditModal(false);
          setEditId(null);
          setEditData(null);
          formik.resetForm();
        }}
        submit={formik.handleSubmit}
        submitText={editId ? Update : Submit}
        disabled={btnLoader}
        loader={btnLoader}
      >
        <form onSubmit={formik.handleSubmit}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {orgCompType === OT_COMP_TYPE.COMPOFF && (
              <div
                style={{
                  padding: "12px 16px",
                  borderRadius: 12,
                  background: "rgba(245, 158, 11, 0.08)",
                  border: "1px solid rgba(245, 158, 11, 0.25)",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                }}
              >
                <AlertCircle
                  size={16}
                  strokeWidth={2.5}
                  style={{ color: "#d97706", flexShrink: 0, marginTop: 1 }}
                />
                <span
                  style={{
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: "#d97706",
                    lineHeight: "1.4",
                  }}
                >
                  Created Overtime policy only apply if super admin select Overtime policy to
                  Overtime Payment.
                </span>
              </div>
            )}
            {/* Section 1  Basic Information */}
            <div
              style={{
                padding: "16px",
                borderRadius: 12,
                background: "var(--glass-bg)",
                border: "1px solid var(--glass-border)",
              }}
            >
              <SectionHeading icon={AlertCircle} title={overtimeLabel.basicInfo} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <BaseInput
                  name="policy_name"
                  label={overtimeLabel.policyName}
                  placeholder={InputPlaceHolder(overtimeLabel.policyName)}
                  value={formik.values.policy_name}
                  handleChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.policy_name}
                  error={formik.errors.policy_name}
                  required
                />
                <BaseSelect
                  name="is_active"
                  label={overtimeLabel.isActive}
                  placeholder={SelectPlaceHolder(overtimeLabel.isActive)}
                  options={[
                    { value: "true", label: "Active" },
                    { value: "false", label: "Inactive" },
                  ]}
                  value={String(formik.values.is_active)}
                  handleChange={(field, val) => formik.setFieldValue(field, val === "true")}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.is_active}
                  error={formik.errors.is_active}
                />
                <div style={{ gridColumn: "1 / -1" }}>
                  <BaseInput
                    name="description"
                    label={overtimeLabel.description}
                    placeholder={InputPlaceHolder(overtimeLabel.description)}
                    value={formik.values.description}
                    handleChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    touched={formik.touched.description}
                    error={formik.errors.description}
                  />
                </div>
              </div>
            </div>

            {/* Section 2 � Thresholds & Limits */}
            <div
              style={{
                padding: "16px",
                borderRadius: 12,
                background: "var(--glass-bg)",
                border: "1px solid var(--glass-border)",
              }}
            >
              <SectionHeading icon={Clock} title={overtimeLabel.thresholds} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <BaseInput
                  name="minimum_ot_threshold"
                  label={overtimeLabel.minOtThreshold}
                  type="number"
                  placeholder={InputPlaceHolder(overtimeLabel.minOtThreshold)}
                  value={formik.values.minimum_ot_threshold}
                  handleChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.minimum_ot_threshold}
                  error={formik.errors.minimum_ot_threshold}
                  min={0}
                  max={4}
                  step={0.5}
                  required
                />
                <BaseInput
                  name="max_ot_per_day"
                  label={overtimeLabel.maxOtPerDay}
                  type="number"
                  placeholder={InputPlaceHolder(overtimeLabel.maxOtPerDay)}
                  value={formik.values.max_ot_per_day}
                  handleChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.max_ot_per_day}
                  error={formik.errors.max_ot_per_day}
                  min={0}
                  max={12}
                  step={0.5}
                  required
                />
                <BaseInput
                  name="weekly_ot_limit"
                  label={overtimeLabel.weeklyOtLimit}
                  type="number"
                  placeholder={InputPlaceHolder(overtimeLabel.weeklyOtLimit)}
                  value={formik.values.weekly_ot_limit}
                  handleChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.weekly_ot_limit}
                  error={formik.errors.weekly_ot_limit}
                  min={0}
                  max={60}
                  step={1}
                  required
                />
              </div>
            </div>

            {/* Section 3 — Calculation Settings */}
            <div
              style={{
                padding: "16px",
                borderRadius: 12,
                background: "var(--glass-bg)",
                border: "1px solid var(--glass-border)",
              }}
            >
              <SectionHeading icon={AlertCircle} title={overtimeLabel.calculation} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <BaseSelect
                  name="calculation_type"
                  label={overtimeLabel.calculationType}
                  placeholder={SelectPlaceHolder(overtimeLabel.calculationType)}
                  options={calculationTypeOptions.map((o) => ({ value: o.value, label: o.label }))}
                  value={formik.values.calculation_type}
                  handleChange={formik.setFieldValue}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.calculation_type}
                  error={formik.errors.calculation_type}
                  required
                />
                <BaseSelect
                  name="approval_type"
                  label={overtimeLabel.approvalType}
                  placeholder={SelectPlaceHolder(overtimeLabel.approvalType)}
                  options={approvalTypeOptions.map((o) => ({ value: o.value, label: o.label }))}
                  value={formik.values.approval_type}
                  handleChange={formik.setFieldValue}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.approval_type}
                  error={formik.errors.approval_type}
                  required
                />
              </div>
            </div>

            {/* Section 4 — Compensation (only in compoff mode) */}
            {!isPayment && (
              <div
                style={{
                  padding: "16px",
                  borderRadius: 12,
                  background: "rgba(99,102,241,0.04)",
                  border: "1px solid rgba(99,102,241,0.15)",
                }}
              >
                <SectionHeading icon={Calendar} title={overtimeLabel.compensation} />
                <div>
                  <BaseSelect
                    name="compoff_rate"
                    label={overtimeLabel.compoffRate}
                    placeholder={SelectPlaceHolder(overtimeLabel.compoffRate)}
                    options={compoffRateOptions.map((o) => ({
                      value: String(o.value),
                      label: o.label,
                    }))}
                    value={String(formik.values.compoff_rate)}
                    handleChange={(field, val) =>
                      formik.setFieldValue(field, val ? Number(val) : 1.5)
                    }
                    handleBlur={formik.handleBlur}
                    touched={formik.touched.compoff_rate}
                    error={formik.errors.compoff_rate}
                    required
                  />
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: "0.75rem",
                      color: "var(--text-muted)",
                      lineHeight: 1.5,
                    }}
                  >
                    {overtimeLabel.compoffRateDesc}
                  </p>
                </div>
              </div>
            )}

            {/* Section 5 — Approval & Automation */}
            <div
              style={{
                padding: "16px",
                borderRadius: 12,
                background: "var(--glass-bg)",
                border: "1px solid var(--glass-border)",
              }}
            >
              <SectionHeading icon={AlertCircle} title={overtimeLabel.approval} />
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <ToggleRow
                  label={overtimeLabel.autoOt}
                  description="Automatically record overtime from attendance without manual entry"
                  checked={formik.values.auto_ot}
                  onChange={() => formik.setFieldValue("auto_ot", !formik.values.auto_ot)}
                />
                <ToggleRow
                  label={overtimeLabel.requiresApproval}
                  description="Overtime must be approved by a manager before it is processed"
                  checked={formik.values.requires_approval}
                  onChange={() =>
                    formik.setFieldValue("requires_approval", !formik.values.requires_approval)
                  }
                />
              </div>
            </div>
          </div>
        </form>
      </BaseModal>

      {/* ── View Modal ── */}
      <BaseModal
        isOpen={viewModal}
        size="lg"
        title={overtimeLabel.viewPolicy}
        toggler={() => {
          setViewModal(false);
          setViewData(null);
        }}
        hideSubmit
        closeText="Close"
      >
        {viewData && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                alignSelf: "flex-start",
                padding: "5px 14px",
                borderRadius: 999,
                fontSize: "0.75rem",
                fontWeight: 700,
                background: isPayment ? "rgba(16,185,129,0.1)" : "rgba(99,102,241,0.1)",
                color: isPayment ? "#10b981" : "var(--brand-indigo)",
                border: `1px solid ${isPayment ? "rgba(16,185,129,0.25)" : "rgba(99,102,241,0.25)"}`,
              }}
            >
              {isPayment ? (
                <>
                  <DollarSign size={11} strokeWidth={2.5} /> {overtimeLabel.compensationPayment}
                </>
              ) : (
                <>
                  <Calendar size={11} strokeWidth={2.5} /> {overtimeLabel.compensationCompoff}
                </>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { label: overtimeLabel.policyName, value: viewData.policy_name },
                {
                  label: overtimeLabel.isActive,
                  value: viewData.is_active ? "Active" : "Inactive",
                },
                { label: overtimeLabel.calculationType, value: viewData.calculation_type },
                { label: overtimeLabel.approvalType, value: viewData.approval_type },
                { label: overtimeLabel.roundingType, value: viewData.rounding_type },
                {
                  label: overtimeLabel.minOtThreshold,
                  value:
                    viewData.minimum_ot_threshold != null
                      ? `${Number(viewData.minimum_ot_threshold)}h`
                      : "—",
                },
                {
                  label: overtimeLabel.maxOtPerDay,
                  value:
                    viewData.max_ot_per_day != null ? `${Number(viewData.max_ot_per_day)}h` : "—",
                },
                {
                  label: overtimeLabel.weeklyOtLimit,
                  value:
                    viewData.weekly_ot_limit != null ? `${Number(viewData.weekly_ot_limit)}h` : "—",
                },
                ...(!isPayment && orgCompType !== OT_COMP_TYPE.COMPOFF
                  ? [
                      {
                        label: overtimeLabel.compoffRate,
                        value:
                          viewData.compoff_rate != null ? `${Number(viewData.compoff_rate)}×` : "—",
                      },
                    ]
                  : []),
                { label: overtimeLabel.autoOt, value: viewData.auto_ot ? "Enabled" : "Disabled" },
                {
                  label: overtimeLabel.requiresApproval,
                  value: viewData.requires_approval ? "Enabled" : "Disabled",
                },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    background: "var(--glass-bg)",
                    border: "1px solid var(--glass-border)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.6875rem",
                      fontWeight: 600,
                      color: "var(--text-muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      marginBottom: 4,
                    }}
                  >
                    {label}
                  </div>
                  <div
                    style={{ fontSize: "0.9375rem", fontWeight: 700, color: "var(--text-primary)" }}
                  >
                    {value ?? "—"}
                  </div>
                </div>
              ))}
              {viewData.description && (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    padding: "10px 14px",
                    borderRadius: 10,
                    background: "var(--glass-bg)",
                    border: "1px solid var(--glass-border)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.6875rem",
                      fontWeight: 600,
                      color: "var(--text-muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      marginBottom: 4,
                    }}
                  >
                    {overtimeLabel.description}
                  </div>
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: "var(--text-secondary)",
                      lineHeight: 1.6,
                    }}
                  >
                    {viewData.description}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </BaseModal>

      {/* ── Table ── */}
      <div
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 16,
          overflow: "hidden",
          padding: "16px",
        }}
      >
        {list && list.length > 0 ? (
          <TableContainer
            totalPages={totalPages}
            totalRecords={totalRecords}
            totalNumberOfRows={totalNumberOfRows}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            fetchData={handleFetchData}
            columns={columns}
            data={searchValue ? searchedList : list}
            isGlobalFilter={true}
            shouldLineBreak={false}
            manualFiltering={true}
            onSearch={handleSearchValueChange}
            customPageSize={customPageSize}
            setCustomPageSize={setCustomPageSize}
            manualPagination={true}
            fetchSortingData={handleFetchSorting}
            SearchPlaceholder={Search}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "60px 20px",
              gap: 12,
            }}
          >
            <AlertCircle size={40} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
            <p style={{ color: "var(--text-muted)", margin: 0, fontWeight: 600 }}>
              {overtimeLabel.noPolicies}
            </p>
            <p style={{ color: "var(--text-muted)", margin: 0, fontSize: "0.8125rem" }}>
              {overtimeLabel.noPoliciesDesc}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OvertimePolicy;
